/**
 * Chat view component for message rendering and streaming.
 */

import { i18n, getLanguage } from './i18n';
import { ApiService, escapeHtml, getClientTimeContext, ToolResultSummary } from './services';
import { Restaurant, RestaurantPhotosResponse, MessageType } from './types';
import { ChatImageData, ChatAudioData, PhotoLightbox } from './ui_components';
import { AudioRecorder } from './audio_recorder';
import DOMPurify from 'dompurify';
import { marked } from 'marked';

// Configure marked for safe rendering
marked.setOptions({
    breaks: true,
    gfm: true,
});

const PHOTO_TAG_REGEX = /\[SHOW_PHOTO:([^\]]+)\]/g;

/**
 * Render markdown to HTML safely.
 */
function renderMarkdown(text: string): string {
    if (!text) return '';
    const html = marked.parse(text) as string;
    const sanitized = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    return sanitized;
}

function inlinePhotoTags(text: string): { text: string; hasTags: boolean } {
    if (!text) return { text: '', hasTags: false };
    let hasTags = false;
    const replaced = text.replace(PHOTO_TAG_REGEX, (_match, placeId: string) => {
        hasTags = true;
        const tooltip = `${i18n.photoHintTooltip} ${placeId}`;
        return `<span class="photo-inline" title="${tooltip}">${i18n.photoHintInline}</span>`;
    });
    return { text: replaced, hasTags };
}

export interface FilterSettings {
    origin_lat: number;
    origin_lng: number;
    max_travel_time_minutes: number;
    travel_mode: string;
}

export interface AddMessageOptions {
    content: string;
    type: MessageType;
    imageDataUrl?: string;
    isCollapsible?: boolean;
    audioDurationSeconds?: number;
    timestamp?: Date;
    toolResults?: ToolResultSummary[] | null;
}

export class ChatView {
    private chatContainer: HTMLElement;
    private api: ApiService;
    private photoLightbox: PhotoLightbox;
    private activeAbortController: AbortController | null = null;

    private pendingRestaurantIds: string[] = [];
    private pendingToolCallChips: HTMLElement[] = [];
    private currentUserMessageElement: HTMLElement | null = null;

    constructor(
        chatContainer: HTMLElement,
        api: ApiService,
        photoLightbox: PhotoLightbox
    ) {
        this.chatContainer = chatContainer;
        this.api = api;
        this.photoLightbox = photoLightbox;
    }

    setCurrentUserMessage(element: HTMLElement | null): void {
        this.currentUserMessageElement = element;
    }

    getCurrentUserMessage(): HTMLElement | null {
        return this.currentUserMessageElement;
    }

    addMessage(options: AddMessageOptions): HTMLElement {
        const {
            content,
            type,
            imageDataUrl,
            isCollapsible = true,
            audioDurationSeconds,
            timestamp,
            toolResults,
        } = options;

        const messageDiv = document.createElement('div');
        messageDiv.className = `message ${type}`;

        // Add timestamp for user messages
        if (type === 'user' && timestamp) {
            const timestampDiv = document.createElement('div');
            timestampDiv.className = 'message-timestamp';
            timestampDiv.textContent = this.formatMessageTimestamp(timestamp);
            messageDiv.appendChild(timestampDiv);
        }

        // Add image if provided
        if (imageDataUrl) {
            const img = document.createElement('img');
            img.className = 'message-image';
            img.src = imageDataUrl;
            img.alt = 'Uploaded image';
            // Add click handler to open in fullscreen lightbox
            img.addEventListener('click', () => {
                this.photoLightbox.open([imageDataUrl], 0);
            });
            messageDiv.appendChild(img);
        }

        // Add audio indicator if provided
        if (audioDurationSeconds !== undefined) {
            const audioIndicator = document.createElement('div');
            audioIndicator.className = 'message-audio-indicator';
            audioIndicator.innerHTML = `
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    <line x1="12" y1="19" x2="12" y2="23"/>
                    <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
                <span>${i18n.audio}: ${AudioRecorder.formatDuration(audioDurationSeconds)}</span>
            `;
            messageDiv.appendChild(audioIndicator);
        }

        const contentDiv = document.createElement('div');
        contentDiv.className = 'message-content';

        // Create copy button
        const copyBtn = document.createElement('button');
        copyBtn.className = 'message-copy-btn';
        copyBtn.type = 'button';
        copyBtn.title = i18n.copyMessage;
        copyBtn.innerHTML = `
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
            </svg>
        `;

        // Strip tool calls and photo tags from assistant messages
        let displayContent = content;
        const parsedToolCalls: Array<{ tool: string; args: Record<string, unknown> }> = [];

        const normalizeArgs = (rawArgs: unknown): Record<string, unknown> => {
            if (rawArgs && typeof rawArgs === 'object' && !Array.isArray(rawArgs)) {
                return rawArgs as Record<string, unknown>;
            }
            return {};
        };

        if (type === 'assistant') {
            const toolCallMatches = content.matchAll(/<tool_call>\s*({[\s\S]*?})\s*<\/tool_call>/g);
            for (const match of toolCallMatches) {
                try {
                    const parsed = JSON.parse(match[1]);
                    if (parsed.tool) {
                        parsedToolCalls.push({ tool: parsed.tool, args: parsed.args || {} });
                    }
                } catch {
                    // Ignore parse errors
                }
            }
            const photoTagResult = inlinePhotoTags(displayContent);
            displayContent = photoTagResult.text;
            displayContent = displayContent.replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '');
            displayContent = displayContent.replace(/\n{3,}/g, '\n\n').trim();
        }

        // Render markdown for assistant messages, plain text for user messages
        if (type === 'assistant') {
            contentDiv.innerHTML = renderMarkdown(displayContent);
        } else {
            contentDiv.textContent = displayContent;
        }

        // Store the raw content for copying (use displayContent which has tool calls stripped)
        const textToCopy = displayContent;

        // Add copy button click handler
        copyBtn.addEventListener('click', async (e) => {
            e.stopPropagation();
            try {
                await navigator.clipboard.writeText(textToCopy);
                copyBtn.classList.add('copied');
                copyBtn.title = i18n.copiedMessage;
                setTimeout(() => {
                    copyBtn.classList.remove('copied');
                    copyBtn.title = i18n.copyMessage;
                }, 2000);
            } catch (err) {
                console.error('Failed to copy message:', err);
            }
        });

        // Check if message should be collapsed
        const lineCount = content.split('\n').length;
        const isLatest = this.chatContainer.children.length === 0 || type === 'user';
        const shouldCollapse = type === 'system' || (isCollapsible && lineCount > 6 && !isLatest);

        // Create message body wrapper to hold content and copy button
        const messageBody = document.createElement('div');
        messageBody.className = 'message-body';
        messageBody.appendChild(contentDiv);
        messageBody.appendChild(copyBtn);

        if (shouldCollapse) {
            contentDiv.classList.add('collapsed');

            const toggleBtn = document.createElement('button');
            toggleBtn.className = 'message-collapse-toggle';
            toggleBtn.textContent = i18n.expand;
            toggleBtn.type = 'button';

            toggleBtn.addEventListener('click', () => {
                const isCollapsed = contentDiv.classList.contains('collapsed');
                contentDiv.classList.toggle('collapsed');
                toggleBtn.textContent = isCollapsed ? i18n.collapse : i18n.expand;
            });

            messageDiv.appendChild(messageBody);
            messageDiv.appendChild(toggleBtn);
        } else {
            messageDiv.appendChild(messageBody);
        }

        // Add tool call chips for assistant messages (historical - show actual results when available)
        const chipsToRender: Array<{
            tool: string;
            args: Record<string, unknown>;
            state: 'executing' | 'success' | 'error';
            resultMessage?: string;
            resultData?: Record<string, unknown>;
        }> = [];

        if (toolResults && toolResults.length > 0) {
            for (const result of toolResults) {
                const parsedFallback = parsedToolCalls.shift();
                const toolName = typeof result.data?.tool === 'string'
                    ? result.data.tool
                    : parsedFallback?.tool || 'unknown_tool';
                const args = normalizeArgs(result.data?.args) || normalizeArgs(parsedFallback?.args);
                chipsToRender.push({
                    tool: toolName,
                    args,
                    state: result.success ? 'success' : 'error',
                    resultMessage: result.message,
                    resultData: result.data ?? undefined
                });
            }
        } else if (parsedToolCalls.length > 0) {
            for (const tc of parsedToolCalls) {
                chipsToRender.push({
                    tool: tc.tool,
                    args: normalizeArgs(tc.args),
                    state: 'success'
                });
            }
        }

        if (chipsToRender.length > 0) {
            const toolCallsContainer = document.createElement('div');
            toolCallsContainer.className = 'tool-calls-container';
            for (const chipDef of chipsToRender) {
                const chip = this.createToolCallChip(
                    chipDef.tool,
                    chipDef.args,
                    chipDef.state,
                    chipDef.resultMessage,
                    chipDef.resultData
                );
                toolCallsContainer.appendChild(chip);
            }
            messageDiv.appendChild(toolCallsContainer);
        }

        this.chatContainer.appendChild(messageDiv);
        this.scrollToBottom();

        return messageDiv;
    }

    addErrorMessage(errorText: string): void {
        const errorDiv = document.createElement('div');
        errorDiv.className = 'error-message';
        const isImageError = errorText.toLowerCase().includes('image') ||
            errorText.toLowerCase().includes('invalid_argument');
        const suffix = isImageError ? i18n.imageErrorSuffix : i18n.pleaseTryAgain;
        errorDiv.textContent = `Error: ${errorText}. ${suffix}`;
        this.chatContainer.appendChild(errorDiv);
        this.scrollToBottom();
    }

    renderSystemPrompt(promptParts: { base: string; restaurant_context: string; filtered_list: string }): void {
        const container = document.createElement('div');
        container.className = 'system-prompt-chip';

        const chipHeader = document.createElement('button');
        chipHeader.className = 'system-prompt-chip-header';
        chipHeader.type = 'button';
        chipHeader.innerHTML = `
            <span class="system-prompt-chip-label">${i18n.systemPromptChip}</span>
            <span class="system-prompt-chip-chevron">▼</span>
        `;

        const content = document.createElement('div');
        content.className = 'system-prompt-chip-content collapsed';

        const sections: { title: string; content: string }[] = [];

        if (promptParts.base) {
            sections.push({ title: i18n.systemPromptSectionBase, content: promptParts.base });
        }
        if (promptParts.restaurant_context) {
            sections.push({ title: i18n.systemPromptSectionRestaurants, content: promptParts.restaurant_context });
        }
        if (promptParts.filtered_list) {
            sections.push({ title: i18n.systemPromptSectionFiltered, content: promptParts.filtered_list });
        }

        sections.forEach(section => {
            const sectionDiv = document.createElement('div');
            sectionDiv.className = 'system-prompt-section';

            const sectionHeader = document.createElement('div');
            sectionHeader.className = 'system-prompt-section-header';
            sectionHeader.textContent = section.title;

            const sectionContent = document.createElement('pre');
            sectionContent.className = 'system-prompt-section-content';
            sectionContent.textContent = section.content;

            sectionDiv.appendChild(sectionHeader);
            sectionDiv.appendChild(sectionContent);
            content.appendChild(sectionDiv);
        });

        chipHeader.addEventListener('click', () => {
            const isCollapsed = content.classList.contains('collapsed');
            content.classList.toggle('collapsed');
            container.classList.toggle('expanded', isCollapsed);
            chipHeader.querySelector('.system-prompt-chip-chevron')!.textContent = isCollapsed ? '▲' : '▼';
        });

        container.appendChild(chipHeader);
        container.appendChild(content);

        if (this.currentUserMessageElement && this.currentUserMessageElement.parentNode === this.chatContainer) {
            this.chatContainer.insertBefore(container, this.currentUserMessageElement);
        } else {
            this.chatContainer.appendChild(container);
        }
        this.scrollToBottom();
    }

    renderInjectedMessages(messages: Array<{ type: string; content: string }>): void {
        const wrapper = document.createElement('div');
        wrapper.className = 'injected-messages-container';

        for (const msg of messages) {
            const container = document.createElement('div');
            container.className = `injected-message-chip injected-message-${msg.type}`;

            let label: string;
            let icon: string;
            switch (msg.type) {
                case 'context':
                    label = i18n.injectedContextLabel;
                    icon = '📋';
                    break;
                case 'visits':
                    label = i18n.injectedVisitsLabel;
                    icon = '📋';
                    break;
                case 'filter':
                    label = i18n.injectedFilterLabel;
                    icon = '📍';
                    break;
                case 'filter_update':
                    label = i18n.injectedFilterUpdateLabel;
                    icon = '📍';
                    break;
                default:
                    label = msg.type;
                    icon = '💬';
            }

            const chipHeader = document.createElement('button');
            chipHeader.className = 'injected-message-chip-header';
            chipHeader.type = 'button';
            chipHeader.innerHTML = `
                <span class="injected-message-chip-icon">${icon}</span>
                <span class="injected-message-chip-label">${label}</span>
                <span class="injected-message-chip-chevron">▼</span>
            `;

            const content = document.createElement('div');
            content.className = 'injected-message-chip-content collapsed';

            const contentPre = document.createElement('pre');
            contentPre.className = 'injected-message-content-text';
            contentPre.textContent = msg.content;
            content.appendChild(contentPre);

            chipHeader.addEventListener('click', () => {
                const isCollapsed = content.classList.contains('collapsed');
                content.classList.toggle('collapsed');
                container.classList.toggle('expanded', isCollapsed);
                chipHeader.querySelector('.injected-message-chip-chevron')!.textContent = isCollapsed ? '▲' : '▼';
            });

            container.appendChild(chipHeader);
            container.appendChild(content);
            wrapper.appendChild(container);
        }

        if (this.currentUserMessageElement && this.currentUserMessageElement.parentNode === this.chatContainer) {
            this.chatContainer.insertBefore(wrapper, this.currentUserMessageElement);
        } else {
            this.chatContainer.appendChild(wrapper);
        }
        this.scrollToBottom();
    }

    createToolCallChip(
        toolName: string,
        args: Record<string, unknown>,
        state: 'executing' | 'success' | 'error' = 'executing',
        resultMessage?: string,
        resultData?: Record<string, unknown>
    ): HTMLElement {
        const chip = document.createElement('div');
        chip.className = `tool-call-chip ${state}`;

        const displayName = toolName.split('_').map(word =>
            word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');

        const stateIcon = state === 'executing' ? '⏳' : state === 'success' ? '✓' : '✗';

        const header = document.createElement('div');
        header.className = 'tool-call-chip-header';
        header.innerHTML = `
            <span class="tool-call-chip-state-icon">${stateIcon}</span>
            <span class="tool-call-chip-name">${displayName}</span>
            ${resultMessage ? `<span class="tool-call-chip-result">${resultMessage}</span>` : ''}
            <span class="tool-call-chip-chevron">▼</span>
        `;

        const details = document.createElement('div');
        details.className = 'tool-call-chip-details collapsed';

        const callLabel = document.createElement('div');
        callLabel.className = 'tool-call-chip-section-label tool-call-chip-call-label';
        callLabel.textContent = i18n.toolCallLabel;

        const callPre = document.createElement('pre');
        callPre.className = 'tool-call-chip-args';
        callPre.textContent = JSON.stringify(args, null, 2);

        const resultLabel = document.createElement('div');
        resultLabel.className = 'tool-call-chip-section-label tool-call-chip-result-label';
        resultLabel.textContent = i18n.toolResultLabel;
        if (!resultMessage && !resultData) {
            resultLabel.style.display = 'none';
        }

        const resultPre = document.createElement('pre');
        resultPre.className = 'tool-call-chip-result-details';
        if (resultMessage || resultData) {
            resultPre.textContent = JSON.stringify({ message: resultMessage, data: resultData }, null, 2);
        } else {
            resultPre.textContent = '';
            resultPre.style.display = 'none';
        }

        details.appendChild(callLabel);
        details.appendChild(callPre);
        details.appendChild(resultLabel);
        details.appendChild(resultPre);

        header.addEventListener('click', () => {
            const isCollapsed = details.classList.contains('collapsed');
            details.classList.toggle('collapsed');
            chip.classList.toggle('expanded', isCollapsed);
            const chevron = header.querySelector('.tool-call-chip-chevron');
            if (chevron) chevron.textContent = isCollapsed ? '▲' : '▼';
        });

        chip.appendChild(header);
        chip.appendChild(details);
        return chip;
    }

    updateToolCallChipState(
        chip: HTMLElement,
        state: 'success' | 'error',
        resultMessage: string,
        resultData?: Record<string, unknown>
    ): void {
        chip.classList.remove('executing');
        chip.classList.add(state);

        const stateIcon = chip.querySelector('.tool-call-chip-state-icon');
        if (stateIcon) {
            stateIcon.textContent = state === 'success' ? '✓' : '✗';
        }

        const chevron = chip.querySelector('.tool-call-chip-chevron');
        if (chevron) {
            const resultSpan = document.createElement('span');
            resultSpan.className = 'tool-call-chip-result';
            resultSpan.textContent = resultMessage;
            chevron.parentElement?.insertBefore(resultSpan, chevron);
        }

        const resultLabel = chip.querySelector('.tool-call-chip-result-label') as HTMLElement | null;
        const resultPre = chip.querySelector('.tool-call-chip-result-details') as HTMLElement | null;
        if (resultLabel) resultLabel.style.display = '';
        if (resultPre) {
            resultPre.style.display = '';
            resultPre.textContent = JSON.stringify({ message: resultMessage, data: resultData }, null, 2);
        }
    }

    async sendMessageStream(
        content: string,
        messageElement: HTMLElement,
        sessionId: string,
        selectedModel: string,
        selectedUserId: string,
        image?: ChatImageData,
        audio?: ChatAudioData,
        filterSettings?: FilterSettings | null
    ): Promise<string> {
        this.cancelActiveStream();
        this.activeAbortController = new AbortController();

        const timeContext = getClientTimeContext();

        const payload: Record<string, unknown> = {
            content: content,
            session_id: sessionId,
            model: selectedModel,
            language: getLanguage(),
            user_id: selectedUserId,
            client_local_time_iso: timeContext.local_time_iso,
            client_timezone: timeContext.timezone,
            client_tz_offset_minutes: timeContext.tz_offset_minutes
        };

        if (image) {
            payload.image = image;
        }

        if (audio) {
            payload.audio = {
                data: audio.data,
                mime_type: audio.mime_type,
                duration_seconds: audio.duration_seconds
            };
        }

        if (filterSettings) {
            payload.filter_settings = filterSettings;
        }

        let response: Response;
        try {
            response = await this.api.fetchStream('api/chat/stream', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                signal: this.activeAbortController.signal
            });
        } catch (error) {
            this.handleStreamAbort(error);
            throw error;
        }

        const reader = response.body?.getReader();
        if (!reader) {
            throw new Error('No response body');
        }

        const decoder = new TextDecoder();

        let currentMessageElement = messageElement;
        let contentDiv = currentMessageElement.querySelector('.message-content') as HTMLElement | null;
        if (!contentDiv) throw new Error('No content div found');

        let combinedFullText = '';
        let fullText = '';
        let fullThoughts = '';
        let buffer = '';
        let firstChunkReceived = false;
        let thoughtsDiv: HTMLElement | null = null;
        let stopReason: { code?: string; message?: string } | null = null;

        const attachStopReason = (reasonText: string): void => {
            if (!currentMessageElement) return;
            const existing = currentMessageElement.querySelector('.response-stop-reason') as HTMLElement | null;
            if (existing) {
                existing.textContent = reasonText;
                return;
            }
            const pill = document.createElement('div');
            pill.className = 'response-stop-reason';
            pill.textContent = reasonText;
            currentMessageElement.appendChild(pill);
        };

        const startNewAssistantMessage = (): void => {
            currentMessageElement = this.addMessage({ content: '', type: 'assistant' });
            contentDiv = currentMessageElement.querySelector('.message-content') as HTMLElement | null;
            if (!contentDiv) throw new Error('No content div found');
            contentDiv.innerHTML = `<span class="thinking-indicator"><span class="thinking-emoji">🤔</span><span>${i18n.thinking}<span class="thinking-dots"></span></span></span>`;

            fullText = '';
            fullThoughts = '';
            firstChunkReceived = false;
            thoughtsDiv = null;
            stopReason = null;

            this.pendingToolCallChips = [];
            this.scrollToBottom();
        };

        while (true) {
            let readResult: ReadableStreamReadResult<Uint8Array>;
            try {
                readResult = await reader.read();
            } catch (error) {
                this.handleStreamAbort(error);
                throw error;
            }
            const { done, value } = readResult;
            if (done) break;

            buffer += decoder.decode(value, { stream: true });

            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    try {
                        const data = JSON.parse(line.slice(6));

                        // Handle progress events
                        if (data.progress) {
                            let progressText: string;
                            switch (data.progress) {
                                case 'loading_restaurants':
                                    progressText = i18n.progressLoadingRestaurants;
                                    break;
                                case 'building_context':
                                    progressText = i18n.progressBuildingContext;
                                    break;
                                case 'calling_ai':
                                    progressText = i18n.progressCallingAI;
                                    break;
                                default:
                                    progressText = i18n.progressDefault;
                            }
                            contentDiv!.innerHTML = `<span class="thinking-indicator"><span class="thinking-emoji">🤔</span><span>${progressText}<span class="thinking-dots"></span></span></span>`;
                            continue;
                        }

                        // Handle system prompt event (legacy)
                        if (data.system_prompt) {
                            this.renderSystemPrompt(data.system_prompt);
                            continue;
                        }

                        // Handle injected context messages
                        if (data.injected_messages) {
                            this.renderInjectedMessages(data.injected_messages);
                            continue;
                        }

                        // Handle tool result event
                        if (data.tool_result) {
                            const result = data.tool_result;
                            const state = result.success ? 'success' : 'error';

                            if (this.pendingToolCallChips.length > 0) {
                                const chip = this.pendingToolCallChips.shift()!;
                                this.updateToolCallChipState(chip, state, result.message, result.data);
                            } else {
                                const toolName = result.data?.tool || 'unknown_tool';
                                const chip = this.createToolCallChip(
                                    toolName,
                                    result.data?.args || {},
                                    state,
                                    result.message,
                                    result.data
                                );
                                let container = currentMessageElement.querySelector('.tool-calls-container');
                                if (!container) {
                                    container = document.createElement('div');
                                    container.className = 'tool-calls-container';
                                    currentMessageElement.appendChild(container);
                                }
                                container.appendChild(chip);
                            }
                            this.scrollToBottom();
                            continue;
                        }

                        if (data.stop_reason) {
                            stopReason = data.stop_reason as { code?: string };
                            const stopText = this.getStopReasonText(stopReason?.code);
                            attachStopReason(stopText);
                            if (!firstChunkReceived && contentDiv) {
                                contentDiv.innerHTML = `<span class="response-stopped">${escapeHtml(stopText)}</span>`;
                            }
                            continue;
                        }

                        // Agentic continue - start new assistant message
                        if (data.agentic_continue) {
                            startNewAssistantMessage();
                            continue;
                        }

                        // Handle restaurant_ids event
                        if (data.restaurant_ids) {
                            console.log('Received restaurant_ids from SSE:', data.restaurant_ids);
                            this.pendingRestaurantIds = data.restaurant_ids;
                            continue;
                        }

                        if (data.error) {
                            throw new Error(data.error);
                        }
                        if (data.done) {
                            if (stopReason) {
                                const stopText = this.getStopReasonText(stopReason?.code);
                                attachStopReason(stopText);
                                if (!firstChunkReceived && contentDiv) {
                                    contentDiv.innerHTML = `<span class="response-stopped">${escapeHtml(stopText)}</span>`;
                                }
                            } else if (!firstChunkReceived && contentDiv) {
                                contentDiv.innerHTML = `<span class="response-stopped">${i18n.responseStopped}</span>`;
                            }
                            break;
                        }

                        // Handle thought streaming
                        if (data.thought) {
                            if (!thoughtsDiv) {
                                thoughtsDiv = document.createElement('div');
                                thoughtsDiv.className = 'message-thoughts';
                                thoughtsDiv.innerHTML = `
                                    <div class="thoughts-header">
                                        <span class="thoughts-icon">💭</span>
                                        <span class="thoughts-label">${i18n.modelThinking || 'Thinking...'}</span>
                                        <span class="thoughts-chevron">▼</span>
                                    </div>
                                    <div class="thoughts-content"></div>
                                `;
                                contentDiv!.parentElement?.insertBefore(thoughtsDiv, contentDiv);

                                const header = thoughtsDiv.querySelector('.thoughts-header');
                                const thoughtContent = thoughtsDiv.querySelector('.thoughts-content');
                                header?.addEventListener('click', () => {
                                    const isCollapsed = thoughtContent?.classList.contains('collapsed');
                                    thoughtContent?.classList.toggle('collapsed');
                                    const chevron = header.querySelector('.thoughts-chevron');
                                    if (chevron) chevron.textContent = isCollapsed ? '▼' : '▲';
                                });
                            }

                            fullThoughts += data.thought;
                            const thoughtsContent = thoughtsDiv.querySelector('.thoughts-content');
                            if (thoughtsContent) {
                                thoughtsContent.textContent = fullThoughts;
                            }
                            this.scrollToBottom();
                            continue;
                        }

                        if (data.chunk) {
                            if (!firstChunkReceived) {
                                firstChunkReceived = true;
                                contentDiv!.innerHTML = '';

                                if (thoughtsDiv) {
                                    const thoughtContent = thoughtsDiv.querySelector('.thoughts-content');
                                    const chevron = thoughtsDiv.querySelector('.thoughts-chevron');
                                    thoughtContent?.classList.add('collapsed');
                                    if (chevron) chevron.textContent = '▲';
                                    const label = thoughtsDiv.querySelector('.thoughts-label');
                                    if (label) label.textContent = i18n.modelThought || 'Thought';
                                }
                            }
                            fullText += data.chunk;
                            combinedFullText += data.chunk;

                            // Detect new tool calls
                            const toolCallMatches = fullText.matchAll(/<tool_call>\s*({[\s\S]*?})\s*<\/tool_call>/g);
                            let toolCallsContainer = currentMessageElement.querySelector('.tool-calls-container') as HTMLElement | null;
                            const existingChipCount = this.pendingToolCallChips.length +
                                (toolCallsContainer?.querySelectorAll('.tool-call-chip:not(.executing)').length || 0);

                            let matchCount = 0;
                            for (const match of toolCallMatches) {
                                matchCount++;
                                if (matchCount > existingChipCount) {
                                    try {
                                        const parsed = JSON.parse(match[1]);
                                        if (parsed.tool) {
                                            if (!toolCallsContainer) {
                                                toolCallsContainer = document.createElement('div');
                                                toolCallsContainer.className = 'tool-calls-container';
                                                currentMessageElement.appendChild(toolCallsContainer);
                                            }
                                            const chip = this.createToolCallChip(parsed.tool, parsed.args || {}, 'executing');
                                            toolCallsContainer.appendChild(chip);
                                            this.pendingToolCallChips.push(chip);
                                        }
                                    } catch {
                                        // Ignore parse errors
                                    }
                                }
                            }

                            const photoTagResult = inlinePhotoTags(fullText);
                            let displayText = photoTagResult.text;
                            displayText = displayText.replace(/<tool_call>[\s\S]*?<\/tool_call>/g, '');
                            displayText = displayText.replace(/\n{3,}/g, '\n\n').trim();
                            contentDiv!.innerHTML = renderMarkdown(displayText);
                            this.scrollToBottom();
                        }
                    } catch (e) {
                        if (e instanceof SyntaxError) {
                            continue;
                        }
                        throw e;
                    }
                }
            }
        }

        try {
            // Inject restaurant photos if any
            if (this.pendingRestaurantIds.length > 0) {
                await this.injectRestaurantPhotos(currentMessageElement, this.pendingRestaurantIds);
                this.pendingRestaurantIds = [];
            }

            this.pendingToolCallChips = [];

            if (!combinedFullText.trim()) {
                if (stopReason) {
                    return stopReason.message || i18n.responseStopped;
                } else {
                    if (contentDiv) {
                        contentDiv.innerHTML = '';
                    }
                    throw new Error(i18n.noResponseGenerated);
                }
            }

            return combinedFullText;
        } finally {
            this.activeAbortController = null;
        }
    }

    cancelActiveStream(): void {
        if (this.activeAbortController) {
            this.activeAbortController.abort();
            this.activeAbortController = null;
        }
    }

    private handleStreamAbort(error: unknown): void {
        if (error instanceof DOMException && error.name === 'AbortError') {
            throw new Error('chat_stream_aborted');
        }
    }

    private async fetchRestaurantPhotos(restaurantId: string): Promise<string[]> {
        try {
            const data = await this.api.fetch<RestaurantPhotosResponse>(`api/restaurants/${restaurantId}/photos`);
            return data.photos || [];
        } catch (error) {
            console.error(`Error fetching photos for restaurant ${restaurantId}:`, error);
            return [];
        }
    }

    private async fetchRestaurantData(restaurantId: string): Promise<Restaurant | null> {
        try {
            return await this.api.fetch<Restaurant>(`api/restaurants/${restaurantId}`);
        } catch (error) {
            console.error(`Error fetching restaurant data for ${restaurantId}:`, error);
            return null;
        }
    }

    private async injectRestaurantPhotos(
        messageElement: HTMLElement,
        restaurantIds: string[]
    ): Promise<void> {
        console.log('Injecting photos for restaurant IDs:', restaurantIds);

        const fetchPromises = restaurantIds.map(async (id) => {
            const [photos, restaurant] = await Promise.all([
                this.fetchRestaurantPhotos(id),
                this.fetchRestaurantData(id)
            ]);
            return { restaurant, photos };
        });

        const restaurantData = await Promise.all(fetchPromises);

        const photosContainer = document.createElement('div');
        photosContainer.className = 'restaurant-photos-container';

        for (let i = 0; i < restaurantData.length; i++) {
            const { restaurant, photos } = restaurantData[i];

            if (photos.length === 0) continue;

            const restaurantSection = document.createElement('div');
            restaurantSection.className = 'restaurant-photo-section';

            const restaurantTitle = document.createElement('h4');
            restaurantTitle.className = 'restaurant-photo-title';
            restaurantTitle.textContent = `${i + 1}. ${restaurant?.name_en || 'Restaurant'}`;
            restaurantSection.appendChild(restaurantTitle);

            const photoGrid = document.createElement('div');
            photoGrid.className = 'restaurant-photos-grid';

            const restaurantPhotos = photos.slice(0, 3);
            restaurantPhotos.forEach((photoUrl, photoIndex) => {
                const photoContainer = document.createElement('div');
                photoContainer.className = 'restaurant-photo';

                const img = document.createElement('img');
                img.src = photoUrl;
                img.alt = `${restaurant?.name_en || 'Restaurant'} dish`;
                img.loading = 'lazy';

                photoContainer.addEventListener('click', () => {
                    this.photoLightbox.open(restaurantPhotos, photoIndex);
                });

                const attribution = document.createElement('div');
                attribution.className = 'restaurant-photo-attribution';
                attribution.textContent = 'Google';

                photoContainer.appendChild(img);
                photoContainer.appendChild(attribution);
                photoGrid.appendChild(photoContainer);
            });

            restaurantSection.appendChild(photoGrid);
            photosContainer.appendChild(restaurantSection);
        }

        if (photosContainer.children.length > 0) {
            const contentDiv = messageElement.querySelector('.message-content');
            if (contentDiv && contentDiv.parentNode) {
                contentDiv.parentNode.insertBefore(photosContainer, contentDiv.nextSibling);
            }
            this.scrollToBottom();
        }
    }

    scrollToBottom(): void {
        this.chatContainer.scrollTop = this.chatContainer.scrollHeight;
    }

    clear(): void {
        this.chatContainer.innerHTML = '';
    }

    private formatMessageTimestamp(date: Date): string {
        return date.toLocaleTimeString(getLanguage() === 'zh' ? 'zh-CN' : 'en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    }

    private getStopReasonText(code?: string): string {
        switch (code) {
            case 'tool_call_limit':
                return i18n.stopReasonToolCallLimit;
            default:
                return i18n.responseStopped;
        }
    }

    renderWelcomeMessage(restaurantCount: number): string {
        const showOnboarding = restaurantCount < 3;
        return `
            <div class="welcome-message">
                <h2 data-i18n="welcomeTitle">${i18n.welcomeTitle}</h2>
                <p data-i18n="welcomeText">${i18n.welcomeText}</p>
                <p class="example" data-i18n="welcomeExample">${i18n.welcomeExample}</p>
                <p class="example example2" data-i18n="welcomeExample2">${i18n.welcomeExample2}</p>
                <p class="example example3" data-i18n="welcomeExample3">${i18n.welcomeExample3}</p>
                <div class="onboarding${showOnboarding ? '' : ' hidden'}">
                    <h3 data-i18n="onboardingTitle">${i18n.onboardingTitle}</h3>
                    <p class="onboarding-step" data-i18n="onboardingStep1">${i18n.onboardingStep1}</p>
                    <p class="onboarding-step" data-i18n="onboardingStep2">${i18n.onboardingStep2}</p>
                    <p class="onboarding-step" data-i18n="onboardingStep3">${i18n.onboardingStep3}</p>
                </div>
            </div>
        `;
    }

    updateOnboardingVisibility(restaurantCount: number): void {
        const onboarding = this.chatContainer.querySelector('.onboarding');
        if (onboarding) {
            if (restaurantCount < 3) {
                onboarding.classList.remove('hidden');
            } else {
                onboarding.classList.add('hidden');
            }
        }
    }

    addHistoryNotice(createdAt: string): void {
        const notice = document.createElement('div');
        notice.className = 'history-notice';
        notice.innerHTML = `
            <p>${i18n.historyNotice} ${new Date(createdAt).toLocaleString()}</p>
            <p class="history-notice-hint">${i18n.historyHint}</p>
        `;
        this.chatContainer.appendChild(notice);
    }

    addThoughtsToMessage(messageEl: HTMLElement, thoughts: string): void {
        const thoughtsDiv = document.createElement('div');
        thoughtsDiv.className = 'message-thoughts';
        thoughtsDiv.innerHTML = `
            <div class="thoughts-header">
                <span class="thoughts-icon">💭</span>
                <span class="thoughts-label">${i18n.modelThought || 'Thought'}</span>
                <span class="thoughts-chevron">▲</span>
            </div>
            <div class="thoughts-content collapsed">${escapeHtml(thoughts)}</div>
        `;

        const contentDiv = messageEl.querySelector('.message-content');
        if (contentDiv) {
            contentDiv.parentElement?.insertBefore(thoughtsDiv, contentDiv);
        }

        const header = thoughtsDiv.querySelector('.thoughts-header');
        const content = thoughtsDiv.querySelector('.thoughts-content');
        header?.addEventListener('click', () => {
            const isCollapsed = content?.classList.contains('collapsed');
            content?.classList.toggle('collapsed');
            const chevron = header.querySelector('.thoughts-chevron');
            if (chevron) chevron.textContent = isCollapsed ? '▼' : '▲';
        });
    }

    removeHistoryNotice(): void {
        const notice = this.chatContainer.querySelector('.history-notice');
        if (notice) notice.remove();
    }

    removeWelcomeMessage(): void {
        const welcomeMsg = this.chatContainer.querySelector('.welcome-message');
        if (welcomeMsg) welcomeMsg.remove();
    }
}
